/**
 * Centralized, typed access to client-side Vite `import.meta.env` values.
 *
 * Why this exists:
 * - One place to read every `VITE_*` variable, with a single type for each.
 * - Required variables are validated ONCE at module load with a clear error,
 *   instead of failing deep inside a feature with a cryptic runtime crash.
 * - Optional variables are returned RAW (untrimmed, uncoerced) so existing call
 *   sites keep their exact per-site handling (`?.trim()`, `!== 'false'`,
 *   `=== 'true'`, `|| null`, etc.) and runtime behavior is unchanged.
 *
 * Reads are LAZY GETTERS on purpose. Each getter body is a LITERAL
 * `import.meta.env.VITE_*` access, so:
 *  - Vite statically replaces it with the baked value in production builds
 *    (client + SSR bundles); there is no dynamic key lookup.
 *  - Reads happen at call time, exactly like the original inline reads. This
 *    preserves behavior for code that reads env inside functions, and keeps
 *    `vi.stubEnv(...)` working in tests (an eager snapshot would freeze values
 *    at import time and both change semantics and break those tests).
 *
 * NOTE: The Vite built-in flags (`import.meta.env.PROD` / `DEV` / `MODE` / `SSR`)
 * are intentionally left as direct reads at their call sites. Vite replaces them
 * with literals so bundlers can tree-shake dev-only / prod-only branches (e.g.
 * service-worker registration in entry-client.tsx). Routing them through this
 * module would defeat that dead-code elimination. They are re-exposed via
 * `buildEnv` below as read-only convenience flags for NEW code only.
 */

// --- Required-variable validation (runs once, at module load) ---
// Mirrors the throw that used to live in src/integrations/supabase/client.ts.
// Empty string counts as missing (matches the original falsy check).
const REQUIRED_VARS: Array<[keyof ImportMetaEnv, string | undefined]> = [
  ['VITE_SUPABASE_URL', import.meta.env.VITE_SUPABASE_URL],
  [
    'VITE_SUPABASE_PUBLISHABLE_KEY',
    import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
  ],
];

const missingRequired = REQUIRED_VARS.filter(([, value]) => !value).map(
  ([name]) => name,
);

// Fail fast in dev/prod so a misconfigured deploy surfaces a clear error at boot
// instead of a cryptic crash deep in a feature (this replaces the throw that
// used to live in src/integrations/supabase/client.ts). Skipped under Vitest
// (MODE === 'test', the same test detector already used elsewhere in this
// codebase) so that unit tests which stub only the vars they exercise are not
// forced to also define the Supabase vars just to import this module.
if (missingRequired.length > 0 && import.meta.env.MODE !== 'test') {
  throw new Error(
    `[env] Missing required environment variable(s): ${missingRequired.join(', ')}. ` +
      `Set them in .env (see .env.example).`,
  );
}

/**
 * Typed, centralized environment values.
 *
 * Required fields are typed `string` (the module throws at load if they are
 * missing). Optional fields are the raw string or `undefined`.
 */
export const env = {
  get onboardingNoTrialWeeklyOfferEnabled(): string | undefined {
    return import.meta.env.VITE_ONBOARDING_NO_TRIAL_WEEKLY_OFFER_ENABLED;
  },
  // --- Required (validated at module load) ---
  get supabaseUrl(): string {
    return import.meta.env.VITE_SUPABASE_URL as string;
  },
  get supabasePublishableKey(): string {
    return import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;
  },

  // --- Stripe (mode-selected at runtime in src/utils/stripe.ts) ---
  get stripeMode(): string | undefined {
    return import.meta.env.VITE_STRIPE_MODE;
  },
  get stripeLivePublishableKey(): string | undefined {
    return import.meta.env.VITE_STRIPE_LIVE_PUBLISHABLE_KEY;
  },
  get stripeTestPublishableKey(): string | undefined {
    return import.meta.env.VITE_STRIPE_TEST_PUBLISHABLE_KEY;
  },
  /** Legacy single-key fallback; mode is inferred from the key prefix. */
  get stripePublishableKey(): string | undefined {
    return import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
  },

  // --- PayPal ---
  get paypalSubscriptionsEnabled(): string | undefined {
    return import.meta.env.VITE_PAYPAL_SUBSCRIPTIONS_ENABLED;
  },
  get paypalEnv(): string | undefined {
    return import.meta.env.VITE_PAYPAL_ENV;
  },

  // --- Observability ---
  get sentryDsn(): string | undefined {
    return import.meta.env.VITE_SENTRY_DSN;
  },

  // --- PostHog product analytics (src/lib/analytics/posthog.ts) ---
  get posthogKey(): string | undefined {
    return import.meta.env.VITE_POSTHOG_KEY;
  },
  get posthogHost(): string | undefined {
    return import.meta.env.VITE_POSTHOG_HOST;
  },

  // --- Web push ---
  get vapidPublicKey(): string | undefined {
    return import.meta.env.VITE_VAPID_PUBLIC_KEY;
  },

  // --- Cloudflare Turnstile ---
  get turnstileSiteKey(): string | undefined {
    return import.meta.env.VITE_TURNSTILE_SITE_KEY;
  },

  // --- Charla support widget ---
  get charlaPropertyKey(): string | undefined {
    return import.meta.env.VITE_CHARLA_PROPERTY_KEY;
  },
  get charlaEnableLocal(): string | undefined {
    return import.meta.env.VITE_CHARLA_ENABLE_LOCAL;
  },

  // --- Audio assets ---
  get bunnyAudioBaseUrl(): string | undefined {
    return import.meta.env.VITE_BUNNY_AUDIO_BASE_URL;
  },

  // --- Auth redirects ---
  get nativeAuthRedirectUrl(): string | undefined {
    return import.meta.env.VITE_NATIVE_AUTH_REDIRECT_URL;
  },
  get webAuthRelayUrl(): string | undefined {
    return import.meta.env.VITE_WEB_AUTH_RELAY_URL;
  },

  // --- Google sign-in ---
  get googleWebClientId(): string | undefined {
    return import.meta.env.VITE_GOOGLE_WEB_CLIENT_ID;
  },
  get googleIosClientId(): string | undefined {
    return import.meta.env.VITE_GOOGLE_IOS_CLIENT_ID;
  },

  // --- Apple sign-in (web) ---
  get appleWebClientId(): string | undefined {
    return import.meta.env.VITE_APPLE_WEB_CLIENT_ID;
  },
  get appleWebRedirectUrl(): string | undefined {
    return import.meta.env.VITE_APPLE_WEB_REDIRECT_URL;
  },

  // --- QA / feature flags ---
  get enableSubscriptionQaFlags(): string | undefined {
    return import.meta.env.VITE_ENABLE_SUBSCRIPTION_QA_FLAGS;
  },
  get qaNativeOfferMode(): string | undefined {
    return import.meta.env.VITE_QA_NATIVE_OFFER_MODE;
  },
  get qaOnboardingOfferMode(): string | undefined {
    return import.meta.env.VITE_QA_ONBOARDING_OFFER_MODE;
  },
  get enableNativeSubscriptions(): string | undefined {
    return import.meta.env.VITE_ENABLE_NATIVE_SUBSCRIPTIONS;
  },

  // --- RevenueCat (native billing) ---
  get revenueCatApiKey(): string | undefined {
    return import.meta.env.VITE_REVENUECAT_API_KEY;
  },
  get revenueCatAppleApiKey(): string | undefined {
    return import.meta.env.VITE_REVENUECAT_APPLE_API_KEY;
  },
  get revenueCatGoogleApiKey(): string | undefined {
    return import.meta.env.VITE_REVENUECAT_GOOGLE_API_KEY;
  },
  get revenueCatPremiumEntitlementId(): string | undefined {
    return import.meta.env.VITE_REVENUECAT_PREMIUM_ENTITLEMENT_ID;
  },
  get revenueCatOnboardingYearlyOfferingId(): string | undefined {
    return import.meta.env.VITE_REVENUECAT_ONBOARDING_YEARLY_OFFERING_ID;
  },

  get revenueCatAnnualLedOfferingId(): string | undefined {
    return import.meta.env.VITE_REVENUECAT_ANNUAL_LED_OFFERING_ID;
  },

  // Subscription product ID lists (restore/entitlement matching)
  get revenueCatNativeAnnualProductIds(): string | undefined {
    return import.meta.env.VITE_REVENUECAT_NATIVE_ANNUAL_PRODUCT_IDS;
  },
  get revenueCatAndroidAnnualProductIds(): string | undefined {
    return import.meta.env.VITE_REVENUECAT_ANDROID_ANNUAL_PRODUCT_IDS;
  },
  get revenueCatIosAnnualProductIds(): string | undefined {
    return import.meta.env.VITE_REVENUECAT_IOS_ANNUAL_PRODUCT_IDS;
  },
  get revenueCatNativeMonthlyProductIds(): string | undefined {
    return import.meta.env.VITE_REVENUECAT_NATIVE_MONTHLY_PRODUCT_IDS;
  },
  get revenueCatAndroidMonthlyProductIds(): string | undefined {
    return import.meta.env.VITE_REVENUECAT_ANDROID_MONTHLY_PRODUCT_IDS;
  },
  get revenueCatIosMonthlyProductIds(): string | undefined {
    return import.meta.env.VITE_REVENUECAT_IOS_MONTHLY_PRODUCT_IDS;
  },
  get revenueCatNativeWeeklyProductIds(): string | undefined {
    return import.meta.env.VITE_REVENUECAT_NATIVE_WEEKLY_PRODUCT_IDS;
  },
  get revenueCatAndroidWeeklyProductIds(): string | undefined {
    return import.meta.env.VITE_REVENUECAT_ANDROID_WEEKLY_PRODUCT_IDS;
  },
  get revenueCatIosWeeklyProductIds(): string | undefined {
    return import.meta.env.VITE_REVENUECAT_IOS_WEEKLY_PRODUCT_IDS;
  },

  // Direct-purchase product ID lists
  get revenueCatPurchaseNativeAnnualProductIds(): string | undefined {
    return import.meta.env.VITE_REVENUECAT_PURCHASE_NATIVE_ANNUAL_PRODUCT_IDS;
  },
  get revenueCatPurchaseNativeWeeklyProductIds(): string | undefined {
    return import.meta.env.VITE_REVENUECAT_PURCHASE_NATIVE_WEEKLY_PRODUCT_IDS;
  },
  get revenueCatPurchaseNativeMonthlyProductIds(): string | undefined {
    return import.meta.env.VITE_REVENUECAT_PURCHASE_NATIVE_MONTHLY_PRODUCT_IDS;
  },
  get revenueCatPurchaseIosAnnualProductIds(): string | undefined {
    return import.meta.env.VITE_REVENUECAT_PURCHASE_IOS_ANNUAL_PRODUCT_IDS;
  },
  get revenueCatPurchaseIosWeeklyProductIds(): string | undefined {
    return import.meta.env.VITE_REVENUECAT_PURCHASE_IOS_WEEKLY_PRODUCT_IDS;
  },
  get revenueCatPurchaseIosMonthlyProductIds(): string | undefined {
    return import.meta.env.VITE_REVENUECAT_PURCHASE_IOS_MONTHLY_PRODUCT_IDS;
  },
  get revenueCatPurchaseAndroidAnnualProductIds(): string | undefined {
    return import.meta.env.VITE_REVENUECAT_PURCHASE_ANDROID_ANNUAL_PRODUCT_IDS;
  },
  get revenueCatPurchaseAndroidWeeklyProductIds(): string | undefined {
    return import.meta.env.VITE_REVENUECAT_PURCHASE_ANDROID_WEEKLY_PRODUCT_IDS;
  },
  get revenueCatPurchaseAndroidMonthlyProductIds(): string | undefined {
    return import.meta.env.VITE_REVENUECAT_PURCHASE_ANDROID_MONTHLY_PRODUCT_IDS;
  },
};

/**
 * Vite build-mode flags. Re-exposed for NEW code that wants typed access.
 * Existing call sites keep reading `import.meta.env.PROD/DEV/MODE` directly so
 * bundlers can tree-shake dev-only / prod-only branches.
 */
export const buildEnv = {
  get mode(): string {
    return import.meta.env.MODE;
  },
  get isDev(): boolean {
    return import.meta.env.DEV;
  },
  get isProd(): boolean {
    return import.meta.env.PROD;
  },
  get isSsr(): boolean {
    return import.meta.env.SSR;
  },
};

export type Env = typeof env;
